import { Injectable, signal } from '@angular/core';
import { Subject } from 'rxjs';
import type { Feature } from 'geojson';
import type { VlmRegisteredLayer } from './vlm-layer-panel.types';

/**
 * Manages the VLM layer panel and individual feature detail dialogs.
 *
 * Two interaction modes:
 * - openFeatureDetail(feature): opens a dialog for a single clicked feature (map click).
 * - register() + open(): registers all layer features and opens the grid panel (sidebar button).
 */
@Injectable({ providedIn: 'root' })
export class VlmLayerPanelService {
  private readonly _registeredLayer = signal<VlmRegisteredLayer | null>(null);
  private readonly _panelOpen = signal<boolean>(false);

  readonly registeredLayer = this._registeredLayer.asReadonly();
  readonly panelOpen = this._panelOpen.asReadonly();

  /** Emits when a single VLM feature should be shown in a detail dialog. */
  readonly featureDetailTrigger$ = new Subject<Feature>();

  /** Stores the VLM layer data so the grid panel can display it. */
  register(layer: VlmRegisteredLayer): void {
    this._registeredLayer.set(layer);
  }

  /** Opens the grid panel. Requires register() to have been called first. */
  open(): void {
    this._panelOpen.set(true);
  }

  /** Closes the grid panel. */
  close(): void {
    this._panelOpen.set(false);
  }

  /** Triggers opening a detail dialog for the given feature (does not open the grid panel). */
  openFeatureDetail(feature: Feature): void {
    this.featureDetailTrigger$.next(feature);
  }
}
